
import ContactInformationStep from './steps/contact-information/ContactInformationStep.vue';
import FullScreenLoader from 'JS/components/FullScreenLoader.vue';
import GlobalErrorModal from './GlobalErrorModal.vue';
import Icon from 'JS/components/Icon.vue';
import OrderSummaryLine from 'JS/components/order/OrderSummaryLine.vue';
import PaymentStep from './steps/payment/PaymentStep.vue';
import ReviewStep from './steps/review-step/ReviewStep.vue';
import SchedulerHeader from './SchedulerHeader.vue';
import SchedulerProgress  from './SchedulerProgress.vue';
import SelectAppointmentStep from './steps/select-appointment/SelectAppointmentStep.vue';
import SelectServicesStep from './steps/select-services/SelectServicesStep.vue';
import TransitionFade from 'JS/components/transitions/TransitionFade.vue';
import UiButton from 'JS/components/UiButton.vue';
import WorkOrderArea from './work-order/WorkOrderArea.vue';
import { AutomotiveSchedulerStep, IWorkOrderInstallableProductResource, IWorkOrderSummaryResource, PrepaymentOption, WorkOrderErrorType,WorkOrderTotalType,WorkOrderType } from '../types/session';
import { computed, defineComponent } from 'vue';
import { getStepInfo } from '../utils/steps';
import { useBreakpoints } from 'JS/composables/breakpoint';
import { useGlobalLoader } from '../state/global-loader';
import { useOrderStore } from '../state/order';
import type { ISchedulerProgressStep } from '../types/ui';

export default defineComponent({
	name: 'SchedulerApp',
	components: {
		FullScreenLoader,
		SchedulerHeader,
		SchedulerProgress,
		TransitionFade,
		OrderSummaryLine,
		UiButton,
		WorkOrderArea,
		Icon,
		GlobalErrorModal,
	},
	setup() {
		const orderStore = useOrderStore();
		const { loading } = useGlobalLoader();

		const steps: ISchedulerProgressStep[] = [
			AutomotiveSchedulerStep.ContactInfo,
			AutomotiveSchedulerStep.Services,
			AutomotiveSchedulerStep.Appointment,
			AutomotiveSchedulerStep.Payment,
			AutomotiveSchedulerStep.Review,
		]
			.map(st => {
				const { title, step } = getStepInfo(st);
				return ({
					label: title,
					step: step,
					id: `stepIndicator-${step}`,
				});
			});

		const currentStepComponent = computed(() => {
			switch (orderStore.currentStep) {
				case AutomotiveSchedulerStep.ContactInfo:
					return ContactInformationStep;
				case AutomotiveSchedulerStep.Services:
					return SelectServicesStep;
				case AutomotiveSchedulerStep.Appointment:
					return SelectAppointmentStep;
				case AutomotiveSchedulerStep.Payment:
					return PaymentStep;
				case AutomotiveSchedulerStep.Review:
					return ReviewStep;
				default:
					return null;
			}
		});

		const { currentBreakpoint, isMobile } = useBreakpoints();

		const emptyWorkOrderSummary: IWorkOrderSummaryResource = {
			estimatedSubtotalString: "-",
			earnableRewardsPoints: "",
			primaryProductLineItem: null,
			auxCharges: null,
			appliedDiscounts: null,
			thresholdPromos: [],
			savings: 0,
			savingsString: "",
			lineItems: null,
			vehicleInfo: null,
			rewardsMember: false,
			totalIsNotFinal: false,
			disclaimer: null,
			tax: "",
			grandTotal: "",
			totalSavings: "",
			total: { type: WorkOrderTotalType.TBD, prepaymentOption: PrepaymentOption.None },
		};

		const workOrderSummary = computed<IWorkOrderSummaryResource>(() => orderStore.session?.workOrderSummary ?? emptyWorkOrderSummary);
		const primaryProductInfo = computed<IWorkOrderInstallableProductResource | null>(() => orderStore.session?.primaryProductInfo ?? null);

		function goToPreviousStep() {
			orderStore.goToPreviousStep();
		}

		const globalError = computed(() => {
			if (orderStore.globalError?.type !== WorkOrderErrorType.Global || !orderStore.globalError.message) {
				return null;
			}

			return orderStore.globalError;
		});

		const title = computed(() => {
			const apptType = {
				[WorkOrderType.Tire]: 'Tire Installation',
				[WorkOrderType.Battery]: 'Battery Installation',
				[WorkOrderType.ServiceOnly]: 'Automotive Service',
			}[orderStore.session?.type];

			// Don't show title until session has loaded and we know the appointment type
			if (!apptType) {
				return '';
			}

			return `Schedule ${apptType} Appointment`;
		});

		return {
			title,
			globalError,
			orderStore,
			steps,
			currentBreakpoint,
			isMobile,
			loading,
			year: new Date().getFullYear(),
			workOrderSummary,
			currentStepComponent,
			goToPreviousStep,
			primaryProductInfo,
		};
	}
});
